import * as React from "react";
import { useTranslation } from "react-i18next";
import { GitFork, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AdjustmentActivities } from "./AdjustmentActivities";
import { useTaskAdjustments } from "../hooks";

interface AdjustmentsListModalProps {
  taskId: number;
  adjustmentsCount?: number | undefined;
  /** Current viewer's user id — needed to show the "Finish adjustment" button */
  currentUserId?: number | undefined;
  /** When true, each row shows a delete button. Caller computes via canCreateAdjustment(role, parent.status) */
  canDeleteAdjustment?: boolean | undefined;
}

export function AdjustmentsListModal({
  taskId,
  adjustmentsCount,
  currentUserId,
  canDeleteAdjustment,
}: AdjustmentsListModalProps) {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation();

  const { data, isLoading } = useTaskAdjustments(open ? taskId : undefined);
  const adjustments = data ?? [];

  const count = adjustmentsCount ?? 0;
  if (count === 0 && !open) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <GitFork className="h-3.5 w-3.5" />
          {count > 0 ? (
            <span>
              {count} {t("adjustment.adjustmentsCount")}
            </span>
          ) : (
            <span>{t("adjustment.viewAdjustments")}</span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <GitFork className="h-5 w-5" />
            {t("adjustment.adjustmentsTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t("adjustment.adjustmentsDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : adjustments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {t("adjustment.noAdjustments")}
            </p>
          ) : (
            adjustments.map((adj) => (
              <AdjustmentActivities
                key={adj.id}
                adjustment={adj}
                currentUserId={currentUserId}
                canDelete={canDeleteAdjustment}
                onFinishSuccess={() => { setOpen(false); }}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
